import { create } from 'zustand';
import {
  announceSignedOut,
  getAccessToken,
  refreshSession,
  setAccessToken,
  setSessionHandlers,
} from '@/api/client';
import { queryClient } from '@/lib/queryClient';
import {
  getOrganizationMe,
  getSelf,
  postLogout,
  type AuthPayload,
  type RawOrganization,
  type RawUser,
} from '@/api/iam';
import {
  clearPersistedSession,
  isRefreshKnownExpired,
  readRefreshToken,
  readSnapshot,
  writeRefreshToken,
  writeSnapshot,
} from './token-storage';
import {
  MEMBER_TYPES,
  ROLE_CODES,
  USER_TYPES,
  type Consents,
  type MemberType,
  type OrganizationSummary,
  type RoleCode,
  type SessionSnapshot,
  type UserType,
} from './types';

/**
 * OTURUM DEPOSU.
 *
 * Erisim jetonu BURADA TUTULMAZ — src/api/client.ts icinde, yalniz bellekte
 * yasar. Depo kalici olani tutar: yenileme jetonu ve oturum ozeti (bkz.
 * token-storage.ts — localStorage takasi orada acikca yaziyor).
 *
 * `/v1/me` UCU YOK. Soguk acilis bu yuzden ozetten baslar.
 */

export type SessionStatus =
  /** Ilk acilis: ozet okundu, sessiz yenileme sonucu bekleniyor. */
  | 'hydrating'
  /** Oturum yok. */
  | 'anonymous'
  /** Gecerli erisim jetonu var. */
  | 'authenticated';

const asUserType = (value: unknown): UserType =>
  USER_TYPES.includes(value as UserType) ? (value as UserType) : 'customer';

const asRoleCode = (value: unknown): RoleCode =>
  ROLE_CODES.includes(value as RoleCode) ? (value as RoleCode) : 'user';

const asMemberType = (value: unknown): MemberType | null =>
  MEMBER_TYPES.includes(value as MemberType) ? (value as MemberType) : null;

const toOrganization = (raw: RawOrganization | null | undefined): OrganizationSummary | null => {
  if (!raw || raw.id === undefined || raw.id === null) return null;
  const memberType = asMemberType(raw.memberType);
  const name = raw.name ?? raw.organizationName;
  // IKI AYRI ALAN ADI, tek anlam: login yaniti `typeCode` uretir (iam-service.js:111),
  // /v1/organizations/me ise HAM organizasyon satirini dondurur ve orada alan
  // `organizationTypeCode`'dur. Fallback olmadan tip GIRISTE dogru gelip ILK REVALIDATE'te
  // dusuyordu — sessizce: buro yoneticisi "Yetkiler" baglantisini, musteri yoneticisi org
  // geneli gorunumunu kaybediyordu. `name` icin ayni fallback zaten vardi.
  const typeCode = raw.typeCode ?? raw.organizationTypeCode;
  return {
    id: String(raw.id),
    ...(name === undefined ? {} : { name }),
    ...(typeCode === undefined ? {} : { typeCode }),
    ...(memberType === null ? {} : { memberType }),
  };
};

const toUser = (raw: RawUser) => ({
  id: String(raw.id),
  ...(raw.userFullName === undefined ? {} : { userFullName: raw.userFullName }),
  ...(raw.userEmail === undefined ? {} : { userEmail: raw.userEmail }),
  ...(raw.userPhone === undefined ? {} : { userPhone: raw.userPhone }),
  ...(raw.userCity === undefined ? {} : { userCity: raw.userCity }),
  ...(raw.userDistrict === undefined ? {} : { userDistrict: raw.userDistrict }),
});

/**
 * Giris / ilk-giris yanitini oturum ozetine cevirir.
 *
 * `isExchangeProfile` DEGIL `user.userType` esas alinir: ilki sunucunun ayni
 * bilgiden turettigi bir kolaylik alani, ikincisi kaynagin kendisi. Ikisi
 * ayrisirsa kaynaga uyulur.
 */
export const snapshotFromAuthPayload = (payload: AuthPayload): SessionSnapshot | null => {
  const raw = payload.user;
  if (!raw || raw.id === undefined || raw.id === null) return null;

  const organization = toOrganization(payload.organization);
  return {
    user: toUser(raw),
    permissions: Array.isArray(payload.userPermissionList) ? payload.userPermissionList : [],
    organization,
    userType: asUserType(raw.userType ?? (payload.isExchangeProfile ? 'exchange' : 'customer')),
    roleCode: asRoleCode(raw.userRoleCode),
    memberType: organization?.memberType ?? null,
    mustChangePassword: raw.mustChangePassword === true,
    consents: {
      kvkkAccepted: raw.kvkkAccepted === true,
      usageAgreementAccepted: raw.usageAgreementAccepted === true,
    },
  };
};

type SessionState = {
  status: SessionStatus;
  snapshot: SessionSnapshot | null;

  /** Giris / ilk-giris yanitini oturuma cevirir ve kalici hale getirir. */
  adoptAuthPayload: (payload: AuthPayload) => SessionSnapshot | null;
  /** Soguk acilis: ozetten hidrasyon + sessiz yenileme + arka planda tazeleme. */
  bootstrap: () => Promise<void>;
  /** Cikis. Sunucuya haber verilemese bile yerel oturum HER ZAMAN temizlenir. */
  signOut: (options?: { allDevices?: boolean }) => Promise<void>;
  /** Sozlesme onayi kaydedildi — ozeti guncel tut. */
  markConsent: (kind: 'kvkk' | 'usage') => void;
  /** Sunucudan gelen taze bilgiyle ozeti tazeler (arka plan). */
  revalidate: () => Promise<void>;
};

const persist = (snapshot: SessionSnapshot): SessionSnapshot => {
  writeSnapshot(snapshot);
  return snapshot;
};

const clearLocal = (): void => {
  setAccessToken(null);
  clearPersistedSession();
};

// Ayni anda birden fazla arka plan tazelemesi yapilmaz; yenileme her
// donusumde `refreshed` cagiriyor ve o da tazeleme tetikliyor.
let revalidateInFlight: Promise<void> | null = null;

export const useSessionStore = create<SessionState>()((set, get) => ({
  status: 'hydrating',
  snapshot: null,

  adoptAuthPayload: (payload) => {
    const snapshot = snapshotFromAuthPayload(payload);
    if (!snapshot || !payload.accessToken || !payload.refreshToken) return null;
    setAccessToken(payload.accessToken);
    writeRefreshToken(payload.refreshToken, payload.refreshExpiresAt ?? null);
    persist(snapshot);
    set({ snapshot, status: 'authenticated' });
    return snapshot;
  },

  bootstrap: async () => {
    const stored = readSnapshot();
    const refreshToken = readRefreshToken();

    // Yenileme jetonu yoksa ya da suresi KANITLI olarak dolmussa canlandirmaya
    // calismanin anlami yok; bosuna bir istek ve giris ekranina sicrama olurdu.
    if (!refreshToken || isRefreshKnownExpired()) {
      clearLocal();
      set({ snapshot: null, status: 'anonymous' });
      return;
    }

    // IYIMSER HIDRASYON: ozet HEMEN durumda. Kabuk dogru menuyle acilir, dil ve
    // rol dogru olur. Ama `status` 'hydrating' KALIR ve korumalar bu sirada bir
    // yukleniyor ekrani gosterir — korumali icerigi basip sonra giris ekranina
    // firlatmak, kisa bir bekleme ekranindan daha kotu bir deneyim.
    if (stored) set({ snapshot: stored });

    const renewed = await refreshSession();
    if (!renewed) {
      clearLocal();
      set({ snapshot: null, status: 'anonymous' });
      return;
    }

    if (!stored) {
      // Jeton canli ama ozet okunamadi (bozuk/kurcalanmis kayit). Kim oldugumuzu
      // sunucudan ogrenmek zorundayiz; olmazsa oturum yok sayilir.
      const user = await getSelf();
      if (!user) {
        clearLocal();
        set({ snapshot: null, status: 'anonymous' });
        return;
      }
      const rebuilt: SessionSnapshot = {
        user: toUser(user),
        permissions: [],
        organization: null,
        userType: asUserType(user.userType),
        roleCode: asRoleCode(user.userRoleCode),
        memberType: null,
        mustChangePassword: user.mustChangePassword === true,
        consents: {
          kvkkAccepted: user.kvkkAccepted === true,
          usageAgreementAccepted: user.usageAgreementAccepted === true,
        },
      };
      set({ snapshot: persist(rebuilt), status: 'authenticated' });
    } else {
      set({ status: 'authenticated' });
    }

    // ARKA PLANDA TAZELEME — bilincli olarak beklenmez.
    //
    // Ozet bayatlayabilir: sunucuda bir yetki geri alinmis olabilir. Bu bir
    // DENEYIM riski (yanlis menu), GUVENLIK riski degil — sunucu yetkisiz
    // istegi zaten 403 ile reddeder. Bu yuzden acilisi bloklamaz.
    void get().revalidate();
  },

  revalidate: async () => {
    if (revalidateInFlight) return revalidateInFlight;
    revalidateInFlight = (async () => {
      try {
        const user = await getSelf();
        const current = get().snapshot;
        if (!user || !current) return;

        let organization = current.organization;
        let permissions = current.permissions;
        let memberType = current.memberType;

        // KOSUL 2026-08-30'da GENISLEDI: musteri tarafinda da organizasyon var artik
        // (musteri org'u + admin/staff uyeligi). Onceden "musteri hesabinda bu uc 403 doner"
        // diye yalniz exchange icin cagriliyordu; bugun org uyesi olan bir MUSTERI icin de
        // 200 doner ve uye tipi oradan tazelenir. Org'u olmayan (super_admin) icin istek yine
        // hic atilmaz.
        //
        // Yetki KODLARI yine yalniz buro tarafinda anlamli (grant tablosu buro ici yetkiler);
        // musteri org'unda liste pratikte bos ya da yalniz viewAccounts olur.
        //
        // TAZELIK NEREDEN GELIYOR (dogrulandi, iam-service.js:388): bu ucun dondurdugu
        // `membership.permissions`, `caller.callerPermissions`'tir — yani gateway'in
        // TOKEN'dan cozup enjekte ettigi deger, DB'den taze bir okuma DEGIL. Yine de
        // guncel oluyor, cunku ACILISTA once sessiz bir refresh yapiliyor ve refresh
        // rotasyonu kullaniciyi DB'den yeniden okuyor (loadUserForToken).
        //
        // Sonucu: bu ucu refresh OLMADAN yeniden cagirmak yetkileri TAZELEMEZ. Oturum
        // ortasinda geri alinan bir yetki, en gec bir sonraki refresh'te (access TTL 15 dk)
        // yansir. Bu bilincli: menu bir sure bayat kalabilir, ama sunucu yetkisiz istegi
        // zaten 403 ile reddediyor.
        if (asUserType(user.userType) === 'exchange' || current.organization !== null) {
          const org = await getOrganizationMe();
          if (org?.organization) {
            const freshMemberType = org.membership?.memberType;
            organization = toOrganization({
              ...org.organization,
              ...(freshMemberType === undefined ? {} : { memberType: freshMemberType }),
            });
            memberType = asMemberType(freshMemberType);
            permissions = Array.isArray(org.membership?.permissions)
              ? org.membership.permissions
              : [];
          }
        }

        const next: SessionSnapshot = {
          user: toUser(user),
          permissions,
          organization,
          userType: asUserType(user.userType),
          roleCode: asRoleCode(user.userRoleCode),
          memberType,
          mustChangePassword: user.mustChangePassword === true,
          consents: {
            kvkkAccepted: user.kvkkAccepted === true,
            usageAgreementAccepted: user.usageAgreementAccepted === true,
          },
        };
        // Istekler sururken cikis yapildiysa ozet GERI YAZILMAZ: `current` isteklerden ONCE
        // okundu. Yazilsaydi cikis yapilmis oturum hem ekranda hem localStorage'da geri
        // geliyordu (M3, 2026-10-02).
        if (get().snapshot === null) return;
        set({ snapshot: persist(next) });
      } catch {
        // Tazeleme basarisiz: eldeki ozetle devam. Sunucu son sozu soyluyor.
      } finally {
        revalidateInFlight = null;
      }
    })();
    return revalidateInFlight;
  },

  signOut: (options) => {
    // ONCE YERELDE CIK (M3, 2026-10-02). Eskiden sunucu cikisi (20 sn'ye kadar) BEKLENIP ancak
    // sonra yerel oturum temizleniyordu; dugmede geri bildirim yoktu, kullanici birkac kez
    // tikliyordu. /v1/logout erisim jetonu istemiyor (govdedeki yenileme jetonu oturumu
    // adlandiriyor; gateway route-policy'de acik yol), yani yerel jetonu once silmek guvenli.
    // Ikinci tik yok sayilir: oturum zaten kapandiysa yapilacak bir sey yok.
    if (get().status === 'anonymous') return Promise.resolve();
    const refreshToken = readRefreshToken();
    clearLocal();
    set({ snapshot: null, status: 'anonymous' });
    // Komsu sekmeler de ciksin; onceki kullanicinin verisi onbellekte kalmasin.
    announceSignedOut();
    queryClient.clear();
    // Sunucu cikisi arkada. Ulasilamazsa yenileme jetonu kendi suresiyle olur.
    void postLogout({
      refreshToken,
      ...(options?.allDevices === undefined ? {} : { allDevices: options.allDevices }),
    }).catch(() => {});
    return Promise.resolve();
  },

  markConsent: (kind) => {
    const current = get().snapshot;
    if (!current) return;
    const consents: Consents =
      kind === 'kvkk'
        ? { ...current.consents, kvkkAccepted: true }
        : { ...current.consents, usageAgreementAccepted: true };
    set({ snapshot: persist({ ...current, consents }) });
  },
}));

/**
 * Depo <-> istemci dongusunu kiran kayit. client.ts depoyu IMPORT ETMEZ;
 * ihtiyaci olan iki geri cagirimi buradan alir.
 */
setSessionHandlers({
  refreshed: () => {
    // Donusumde basilan yeni erisim jetonu VERITABANINDAN uretiliyor, yani
    // yetkiler orada guncel. Ozeti bununla hizalamak icin iyi bir an.
    if (useSessionStore.getState().status === 'authenticated') {
      void useSessionStore.getState().revalidate();
    }
  },
  lost: () => {
    clearPersistedSession();
    useSessionStore.setState({ snapshot: null, status: 'anonymous' });
  },
});

/** Erisim jetonu istemcide; testler ve teshis icin okunabilir kalsin. */
export { getAccessToken };
